import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectInput,
  Checkbox,
  SelectInputProps,
  DateInput
} from '@lightbeamhealth/blueprint-form';
import Button from '../../../molecules/button';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import Card, { CardBlock } from '../../card';
import { FormatDate, GetCurrentTZ } from '../../../../libs/time';
import { Capitalize } from '../../../../libs/format';
import { BasicNeedsModuleKey } from '../../../../libs/apps/epxbasicneeds';

const DATE_FORMAT = 'YYYY-MM-DD';
const DOY_FORMAT = 'MM/DD';
const { DeserializeDate, SerializeDate } = FormInstance;

type Mode = 'day' | 'week' | 'month' | 'year';

interface ModuleData {
  key?: BasicNeedsModuleKey;
  order?: number;
  meta?: { [key: string]: any };
  step?: number;
  mode?: Mode;
  weekDays?: number[];
  daysOfMonth?: number[];
  date?: string;
  isActive?: boolean;
  isDiscontinued?: boolean;
  discontinuedAt?: Date;
  lastSessionAt?: Date;
  isEditing?: boolean;
}

interface EpxBasicNeedsData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  time: string;
  modules?: {
    [key: string]: ModuleData;
  };
}

type EpxBasicNeedsDataRaw = Omit<
  DateSerialized<EpxBasicNeedsData, 'start' | 'end'>,
  'modules'
> & {
  modules?: {
    [key: string]: Omit<ModuleData, 'date'> & {
      date?: Date;
    };
  };
};

export const EpxBasicNeedsDefaultData: EpxBasicNeedsData = {
  method: 'message',
  language: 'en',
  start: moment().format('YYYY-MM-DD'),
  end: null,
  tz: GetCurrentTZ(),
  time: '11:00',
  modules: {
    checkin: {
      key: 'checkin',
      isActive: true,
      step: 2,
      mode: 'week' as Mode,
      weekDays: [1, 3, 5],
      daysOfMonth: [1],
      date: moment().add(1, 'month').startOf('month').format(DOY_FORMAT)
    },
    food: {
      key: 'food',
      isActive: true,
      step: 1,
      mode: 'month' as Mode,
      weekDays: [1],
      daysOfMonth: [1],
      date: moment().add(1, 'month').startOf('month').format(DOY_FORMAT)
    },
    education: {
      key: 'education',
      isActive: true,
      step: 1,
      mode: 'year' as Mode,
      date: moment().add(1, 'month').startOf('month').format(DOY_FORMAT)
    },
    housing: {
      key: 'housing',
      isActive: true,
      step: 1,
      mode: 'year' as Mode,
      weekDays: [1],
      daysOfMonth: [1],
      date: moment().add(1, 'month').startOf('month').format(DOY_FORMAT)
    },
    employment: {
      key: 'employment',
      isActive: true,
      step: 1,
      mode: 'month' as Mode,
      weekDays: [1],
      daysOfMonth: [1],
      date: moment().add(1, 'month').startOf('month').format(DOY_FORMAT)
    },
    insurance: {
      key: 'insurance',
      isActive: true,
      step: 1,
      mode: 'year' as Mode,
      weekDays: [1],
      daysOfMonth: [1],
      date: moment().add(1, 'month').startOf('month').format(DOY_FORMAT)
    }
  }
};

interface EpxBasicNeedsFormProps {
  setForm?: (form: FormInstance<EpxBasicNeedsDataRaw>) => void;
  onSubmit?: (data: EpxBasicNeedsData) => void;
  isDisabled?: boolean;
}

const EpxBasicNeedsForm = ({
  setForm,
  onSubmit,
  isDisabled
}: EpxBasicNeedsFormProps): JSX.Element => {
  const serialize = (data: EpxBasicNeedsDataRaw): EpxBasicNeedsData => {
    const { modules, ...rest } = data;
    const _data: EpxBasicNeedsData = {
      ...rest,
      modules: {},
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT)
    };
    for (const key of Object.keys(modules)) {
      const _date = modules[key].date;
      _data.modules[key] = {
        ...modules[key],
        date: _date ? SerializeDate(_date, DOY_FORMAT) : undefined
      };
    }
    return _data;
  };
  const deserialize = (data: EpxBasicNeedsData): EpxBasicNeedsDataRaw => {
    const { modules, ...rest } = data;
    const _data: EpxBasicNeedsDataRaw = {
      ...rest,
      modules: {},
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
    for (const key of Object.keys(modules)) {
      const _date = modules[key].date;
      _data.modules[key] = {
        ...modules[key],
        date: _date ? DeserializeDate(_date, DOY_FORMAT) : undefined
      };
    }
    return _data;
  };
  const defaultData = deserialize(EpxBasicNeedsDefaultData);
  const form = new FormInstance<EpxBasicNeedsDataRaw, EpxBasicNeedsData>(
    defaultData,
    { serialize, deserialize }
  );
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <SelectTime label="Message At" name="time" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <h3>MODULES (Check to Enable)</h3>
                {Object.keys(props.values.modules).map((key) => (
                  <ModuleSchedule
                    key={key}
                    module={props.values.modules[key]}
                  />
                ))}
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

interface ModuleScheduleProps {
  module: Omit<ModuleData, 'date'> & {
    date?: Date;
  };
}

const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];

const ModuleSchedule: React.FC<ModuleScheduleProps> = ({ module }) => {
  const key = module.key;
  const [isEditing, setIsEditing] = useState(false);
  const labels = {
    key: Capitalize(module.key),
    mode: Capitalize(module.mode),
    summary: null
  };
  const suffix = (n: number) =>
    n != 11 && n % 10 === 1
      ? `the ${n}st`
      : n != 12 && n % 10 === 2
        ? `the ${n}nd`
        : n != 13 && n % 10 === 3
          ? `the ${n}rd`
          : `the ${n}th`;
  if (module.mode === 'week') {
    const selection = module.weekDays.map((day) => {
      return DAYS[day];
    });
    labels.summary = selection.join(', ');
  } else if (module.mode === 'month') {
    const selection = module.daysOfMonth.map((day) => {
      return suffix(day);
    });
    labels.summary = selection.join(', ');
  } else if (module.mode === 'year') {
    labels.summary = FormatDate(module.date, 'MM/DD');
  }
  const options: Record<string, SelectInputProps['options']> = {};
  options.mode = [
    {
      value: 'week',
      label: 'Weekly'
    },
    {
      value: 'month',
      label: 'Monthly'
    },
    {
      value: 'year',
      label: 'Yearly'
    }
  ];
  options.step = [];
  for (let i = 1; i <= 10; i++) {
    options.step.push({
      value: i,
      label: i == 1 ? `Every ${labels.mode}` : `Every ${i} ${labels.mode}s`
    });
  }
  options.daysOfMonth = [];
  for (let i = 1; i <= 30; i++) {
    options.daysOfMonth.push({
      value: i,
      label: suffix(i)
    });
  }
  return (
    <CardBlock>
      <Row>
        <Col sm={8} className="pt-4">
          <Checkbox
            key={key}
            inline={true}
            name={`modules.${key}.isActive`}
            label={labels.key}
          />
        </Col>
        <Col sm={12} className="pt-4">
          Every {labels.mode} on {labels.summary}
        </Col>
        <Col sm={4}>
          <Button
            fill={true}
            active={isEditing}
            onClick={() => setIsEditing(!isEditing)}
            className={isEditing ? 'intent-primary' : 'intent-warning'}
            text={isEditing ? 'Hide' : 'Customize'}
          />
        </Col>
      </Row>
      {isEditing ? (
        <Card className="mt-6">
          <Row>
            <Col sm={8}>
              <SelectInput
                label="Send Messages"
                name={`modules.${key}.mode`}
                fill={true}
                disabled={!isEditing}
                options={options.mode}
              />
            </Col>
            <Col sm={8}>
              <SelectInput
                label="Frequency"
                name={`modules.${key}.step`}
                fill={true}
                disabled={!isEditing}
                options={options.step}
              />
            </Col>
            <Col sm={8}>
              {module.mode === 'month' ? (
                <SelectInput
                  label="On"
                  name={`modules.${key}.daysOfMonth[0]`}
                  fill={true}
                  disabled={!isEditing}
                  options={options.daysOfMonth}
                />
              ) : module.mode === 'year' ? (
                <DateInput
                  label="On"
                  name={`modules.${key}.date`}
                  format="MM/DD"
                  fill={true}
                  disabled={!isEditing}
                />
              ) : null}
            </Col>
          </Row>
          {module.mode === 'week' ? (
            <Weekday
              label="Days of Week for Check In"
              name={`modules.${key}.weekDays`}
              minItems={1}
              inline
              abbreviated
            />
          ) : null}
        </Card>
      ) : null}
    </CardBlock>
  );
};

export default EpxBasicNeedsForm;
